/* 同步的介面：設定區塊、頂列指示燈、衝突收件匣。 */
import { useState } from "react";
import type { Concept } from "../domain/types.ts";
import { deriveTitle } from "../domain/rules.ts";
import { useI18n } from "../i18n/index.tsx";
import { Icon } from "./icons.tsx";
import type { useSync } from "./useSync.ts";

export type SyncApi = ReturnType<typeof useSync>;

const fmtTime = (ms: number, locale: string) =>
  new Intl.DateTimeFormat(locale === "en" ? "en-US" : "zh-Hant-TW", { dateStyle: "short", timeStyle: "short" }).format(new Date(ms));

export function SyncBadge(props: { sync: SyncApi; onOpen: () => void }) {
  const { t, locale } = useI18n();
  const st = props.sync.state;
  if (!st.configured) return null;
  const label = st.error ? t("sync.error", { message: st.error })
    : st.running ? t("sync.syncing")
    : st.pending ? t("sync.pending", { n: st.pending })
    : st.lastSyncAt ? t("sync.lastAt", { time: fmtTime(st.lastSyncAt, locale) }) : t("sync.never");
  return (
    <button className="iconbtn syncbadge" aria-label={t("sync.title")} title={label} onClick={props.onOpen}>
      <Icon name={st.error ? "warn" : st.running ? "sync" : st.unlocked ? "cloud" : "lock"} />
      {st.pending > 0 && <span className="badge mono">{st.pending}</span>}
    </button>
  );
}

export function SyncSection(props: { sync: SyncApi }) {
  const { t } = useI18n();
  const st = props.sync.state;
  const [serverUrl, setServerUrl] = useState(st.config?.serverUrl ?? "");
  const [passphrase, setPassphrase] = useState("");
  const [confirm, setConfirm] = useState("");
  const [keyIdInput, setKeyIdInput] = useState("");
  const [mode, setMode] = useState<"idle" | "setup" | "join" | "unlock" | "recovery">("idle");
  const [localError, setLocalError] = useState("");

  const err = localError || st.error || "";

  const doCreate = async () => {
    setLocalError("");
    try { await props.sync.createAccount(serverUrl, passphrase, confirm); setMode("idle"); setPassphrase(""); setConfirm(""); }
    catch (e) {
      const raw = e instanceof Error ? e.message : String(e);
      setLocalError(raw.startsWith("sync.") ? t(raw as never) : raw);
    }
  };
  const doJoin = async () => {
    setLocalError("");
    try { await props.sync.joinAccount(serverUrl, keyIdInput, passphrase); setMode("idle"); setPassphrase(""); }
    catch (e) {
      const raw = e instanceof Error ? e.message : String(e);
      setLocalError(raw.startsWith("sync.") ? t(raw as never) : raw);
    }
  };
  const doUnlock = async () => {
    setLocalError("");
    try { await props.sync.unlock(passphrase, mode === "recovery"); setMode("idle"); setPassphrase(""); }
    catch (e) {
      const raw = e instanceof Error ? e.message : String(e);
      setLocalError(raw.startsWith("sync.") ? t(raw as never) : raw);
    }
  };

  return (
    <div className="panel" data-sync-section>
      <h2 style={{ display: "block" }}>{t("sync.title")}</h2>
      <p className="hint">{t("sync.hint")}</p>

      {err && <p className="hint bad" data-sync-error>{err}</p>}

      {/* 伺服器網址：任何狀態都可以改 */}
      <div className="row" style={{ marginTop: 12 }}>
        <input className="input" aria-label={t("sync.serverUrl")} data-sync-serverurl value={serverUrl}
          placeholder={t("sync.serverUrl.placeholder")}
          onChange={(e) => setServerUrl(e.target.value)}
          onBlur={() => void props.sync.setServerUrl(serverUrl)} />
      </div>

      {!st.configured && mode === "idle" && (
        <>
          <p className="hint" style={{ marginTop: 12 }}>{t("sync.notSetup")}</p>
          <div className="row" style={{ marginTop: 12 }}>
            <button className="btn primary" onClick={() => setMode("setup")}>{t("sync.setup")}</button>
            <button className="btn" onClick={() => setMode("join")}>{t("sync.join")}</button>
          </div>
        </>
      )}

      {mode === "setup" && (
        <div style={{ marginTop: 12 }}>
          <p className="hint bad">{t("sync.passphrase.warn")}</p>
          <input className="input" type="password" aria-label={t("sync.passphrase")} placeholder={t("sync.passphrase")}
            value={passphrase} onChange={(e) => setPassphrase(e.target.value)} />
          <input className="input" type="password" aria-label={t("sync.passphrase.confirm")}
            placeholder={t("sync.passphrase.confirm")} style={{ marginTop: 8 }}
            value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          <div className="row" style={{ marginTop: 12 }}>
            <button className="btn primary" disabled={st.busy} onClick={() => void doCreate()} data-sync-create>
              {t("sync.setup")}</button>
            <button className="btn" onClick={() => { setMode("idle"); setLocalError(""); }}>{t("edit.cancel")}</button>
          </div>
        </div>
      )}

      {mode === "join" && (
        <div style={{ marginTop: 12 }}>
          <input className="input" aria-label={t("sync.join.keyId")} placeholder={t("sync.join.keyId")}
            value={keyIdInput} onChange={(e) => setKeyIdInput(e.target.value)} data-sync-keyid-input />
          <input className="input" type="password" aria-label={t("sync.passphrase")} placeholder={t("sync.passphrase")}
            style={{ marginTop: 8 }} value={passphrase} onChange={(e) => setPassphrase(e.target.value)} />
          <div className="row" style={{ marginTop: 12 }}>
            <button className="btn primary" disabled={st.busy} onClick={() => void doJoin()} data-sync-join>
              {t("sync.join")}</button>
            <button className="btn" onClick={() => { setMode("idle"); setLocalError(""); }}>{t("edit.cancel")}</button>
          </div>
        </div>
      )}

      {st.configured && !st.unlocked && mode === "idle" && (
        <div style={{ marginTop: 12 }}>
          <p className="hint mono" data-sync-keyid>{t("sync.keyId")}：{st.keyId}</p>
          <div className="row" style={{ marginTop: 12 }}>
            <button className="btn primary" onClick={() => setMode("unlock")}>{t("sync.unlock")}</button>
            <button className="btn" onClick={() => setMode("recovery")}>{t("sync.unlock.recovery")}</button>
          </div>
        </div>
      )}

      {(mode === "unlock" || mode === "recovery") && (
        <div style={{ marginTop: 12 }}>
          <input className="input" type="password" aria-label={t("sync.passphrase")}
            placeholder={mode === "recovery" ? t("sync.recovery.title") : t("sync.passphrase")}
            value={passphrase} onChange={(e) => setPassphrase(e.target.value)} data-sync-secret />
          <div className="row" style={{ marginTop: 12 }}>
            <button className="btn primary" disabled={st.busy} onClick={() => void doUnlock()} data-sync-unlock>
              {mode === "recovery" ? t("sync.unlock.recovery") : t("sync.unlock")}</button>
            <button className="btn" onClick={() => { setMode("idle"); setLocalError(""); }}>{t("edit.cancel")}</button>
          </div>
        </div>
      )}

      {st.configured && st.unlocked && (
        <div style={{ marginTop: 12 }}>
          <p className="hint"><span className="mono">{t("sync.unlocked")}</span>
            {"　"}{st.lastSyncAt ? t("sync.lastAt", { time: fmtTime(st.lastSyncAt, locale0()) })
              : t("sync.never")}
            {"　"}{st.pending > 0 ? t("sync.pending", { n: st.pending }) : ""}</p>
          <p className="hint mono" data-sync-keyid>{t("sync.keyId")}：{st.keyId}</p>
          <div className="row" style={{ marginTop: 12 }}>
            <button className="btn primary" disabled={st.running} onClick={() => void props.sync.syncNow()} data-sync-now>
              {st.running ? t("sync.syncing") : t("sync.now")}</button>
            <button className="btn" onClick={() => props.sync.lock()}>{t("sync.lock")}</button>
          </div>
          {st.lastResult?.error && <p className="hint bad">{t("sync.error", { message: st.lastResult.error })}</p>}
        </div>
      )}
    </div>
  );
}

/** 救援碼：只顯示一次，必須確認已抄下。 */
export function RecoveryPanel(props: { code: string; onConfirm: () => void }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  return (
    <section className="sheetpanel" data-recovery-panel>
      <h2>{t("sync.recovery.title")}</h2>
      <p className="hint bad">{t("sync.recovery.hint")}</p>
      <p className="recovery mono" data-recovery-code>{props.code}</p>
      <div className="row">
        <button className="btn" onClick={() => {
          void navigator.clipboard?.writeText(props.code).then(() => setCopied(true)).catch(() => setCopied(false));
        }}>{copied ? t("sync.recovery.copied") : t("sync.recovery.copy")}</button>
        <button className="btn primary" onClick={props.onConfirm} data-recovery-confirm>
          {t("sync.recovery.confirm")}</button>
      </div>
    </section>
  );
}

export function ConflictPanel(props: {
  copies: Concept[]; originals: (id: string) => Concept | undefined;
  onTake: (copy: Concept) => void; onDiscard: (copy: Concept) => void; onKeepBoth: (copy: Concept) => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  return (
    <section className="sheetpanel">
      <div className="row between">
        <h2>{t("sync.conflicts.title")}</h2>
        <button className="iconbtn" onClick={props.onClose} aria-label={t("settings.title")}>✕</button>
      </div>
      <p className="hint">{t("sync.conflicts.hint")}</p>
      {props.copies.length === 0 && <p className="hint dim">{t("sync.conflicts.none")}</p>}
      {props.copies.map((copy) => {
        const original = copy.conflictOf ? props.originals(copy.conflictOf) : undefined;
        return (
          <div className="panel" key={copy.id} data-conflict={copy.id}>
            <div className="conflict">
              <div className="side">
                <h3>{t("sync.conflicts.keepBoth")}·{t("history.current")}</h3>
                <p>{original ? original.summary : "—"}</p>
                <p className="hint">{original ? deriveTitle(original.note) : ""}</p>
              </div>
              <div className="side">
                <h3>{t("sync.conflicts.title")}</h3>
                <p>{copy.summary}</p>
                <p className="hint">{deriveTitle(copy.note)}</p>
              </div>
            </div>
            <div className="row" style={{ marginTop: 12 }}>
              <button className="btn primary" onClick={() => props.onTake(copy)}>{t("sync.conflicts.take")}</button>
              <button className="btn" onClick={() => props.onKeepBoth(copy)}>{t("sync.conflicts.keepBoth")}</button>
              <button className="btn" onClick={() => props.onDiscard(copy)}>{t("sync.conflicts.discard")}</button>
            </div>
          </div>
        );
      })}
    </section>
  );
}

/* useI18n 的 locale 在某些呼叫點不方便取得，這裡給 SyncSection 一個安全取值 */
function locale0(): string {
  return typeof document !== "undefined" && document.documentElement.lang === "en" ? "en" : "zh-Hant";
}
